import {
  Box,
  DialogContent,
  IconButton,
  Typography,
  Backdrop,
  useMediaQuery,
  useTheme,
  Slide,
} from "../ui";
import CloseIcon from "@mui/icons-material/Close";
import type { Company } from "@/pages/private/dashboard/SharePage";
import React from "react";
import { Dialog, DialogTitle } from "@ui";
import { ThemeColours } from "@/clients/acme/config/colours";
import { Divider } from "@mui/material";

interface InfoDialogProps {
  open: boolean;
  onClose: () => void;
  company: Company;
  setOpenUUDeclarationDialog?: (arg0: boolean) => void;
  infoText?: string;
  maxWidth?: string;
}

const Transition = React.forwardRef<
  unknown,
  { in?: boolean; children: React.ReactElement }
>(function Transition(props, ref) {
  return <Slide direction="up" ref={ref as React.Ref<unknown>} {...props} />;
});

const InfoDialog: React.FC<InfoDialogProps> = ({
  open,
  onClose,
  company,
  setOpenUUDeclarationDialog,
  infoText,
  maxWidth,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  function openDeclarationModal() {
    onClose();
    setOpenUUDeclarationDialog(true);
  }

  const getContent = (): JSX.Element[] => {
    const specialContent: Record<string, JSX.Element> = {
      "United Utilities": (
        <>
          <Typography
            key="default"
            sx={{
              fontWeight: 500,
              fontSize: 16,
              color: ThemeColours.navy1,
              mb: 1.5,
              pt: 2,
              lineHeight: 1.3,
            }}
          >
            The documents you upload serve as essential supporting evidence to
            verify your budget details. You must select{" "}
            <strong>at least one document</strong> to share you information with
            United Utilities.
          </Typography>
          <Typography
            key="default"
            sx={{
              fontWeight: 500,
              fontSize: 16,
              color: ThemeColours.navy1,
              mb: 1.5,
              lineHeight: 1.3,
            }}
          >
            If you wish to <strong>change or add documents</strong> to share,
            please follow these steps:
          </Typography>
          <Typography
            key="default"
            sx={{
              fontWeight: 500,
              fontSize: 16,
              color: ThemeColours.navy1,
              mb: 1,
              lineHeight: 1.8,
            }}
          >
            1. Navigate to the{" "}
            <u style={{ cursor: "pointer" }} onClick={openDeclarationModal}>
              Declaration Form
            </u>{" "}
            <br></br>
            2. Remove or upload the desired documents <br></br>
            3. Re-accept the declaration to confirm your changes
          </Typography>
        </>
      ),
      "NDL-INFO": (
        <>
          <Typography
            key="default"
            sx={{
              fontWeight: 500,
              fontSize: 16,
              color: ThemeColours.navy1,
              mb: 1.5,
              pt: 2,
              lineHeight: 1.3,
            }}
          >
            By sharing your information with National Debtline, you can receive
            a free call from their expert advisers. Pick the time that suits
            you, and they’ll contact you to help with your debt worries.
          </Typography>
        </>
      ),
    };

    const defaultContent = (
      <Typography
        key="default"
        sx={{
          fontWeight: 500,
          fontSize: 16,
          color: ThemeColours.navy1,
          mb: 1,
          pt: 2,
          lineHeight: 1.3,
        }}
      >
        The documents you upload help verify your budget details and will be
        securely shared with the company.
      </Typography>
    );

    return [specialContent[company.name] || defaultContent];
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth || "sm"}
      fullWidth
      keepMounted
      PaperProps={{
        sx: {
          ...(isMobile && {
            margin: 0,
            width: "100%",
            maxWidth: "100%",
            maxHeight: "90vh",
            borderRadius: "16px 16px 0 0",
            position: "fixed",
            bottom: 0,
          }),
          ...(!isMobile && {
            maxHeight: "90vh",
            padding: "0px 0px 0px 0px",
          }),
        },
      }}
      TransitionComponent={Transition as any}
      BackdropComponent={Backdrop}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <DialogTitle
          sx={{
            px: 4,
            justifyContent: "space-between",
            display: "flex",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: ThemeColours.titleVar2,
              width: "100%",
            }}
          >
            Information
          </Typography>

          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: "#9ca3af",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider
          sx={{
            marginLeft: 4,
            marginRight: 5,
            borderColor: "#CCCCCC",
            borderBottomWidth: 2,
          }}
        />

        <DialogContent sx={{ pt: 1, px: 4, flexGrow: 1, overflowY: "auto" }}>
          {company.name != "United Utilities Info" ? (
            getContent()
          ) : (
            <>
              <div dangerouslySetInnerHTML={{ __html: infoText }} />
            </>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default InfoDialog;
